// KB-4319 — kept apart from prepareOfferDataCore.ts on purpose. That file, offerDataPreparationServer.ts and the web
// offerDataPreparation.ts must never name the pipeline-owned band-statement field outside comments: the preparer's
// output is the full intended map for five offer-rewrite paths and the clone path, and the census guard
// `scripts/camp-data/_reconcile/offerComputeCore.observedBands.test.cjs` ("the ADMIN preparer must never emit
// observedBands") pins it. This module READS that field on the stored doc as provenance and returns only the core's
// observed* age/grade INPUTS; it never returns, forwards or spreads the record itself (pinned behaviourally by
// functions/src/shared/offer/__tests__/prepareOfferDataCore.kb4319.test.ts).
import type { CoreInput } from "./prepareOfferDataCore";

/**
 * KB-4319 — a camp-data session's own age/grade band on a rebuild of an EXISTING offer, as the core's observed
 * inputs. Both rebuild seams (`prepareOfferDataServer`, the web `prepareOfferData`) rebuild `variationData` from the
 * program, and prepareOfferDataCore's blocks apply a session's own ages (KB-1592) and grades (KB-2107) only from `observed*`
 * inputs, which only the pipeline writer passed — so every "Publish to offers" reset adapter-native sessions to the
 * program's band (Young Writers' Institute's Track-Out 8–11, grades 3–6, came back 8–14 with no grades).
 *
 * Per side, from `doc` (the stored, pre-edit offer — as KB-3733 reads a session's price):
 *   - age: `variationData.<side>AgeInYears` when `agesAreOverrides` is true as this `write` leaves it (equal to the
 *     stored flag on a Publish, the edit's on a session-dialog save) — the flag the writer stamps exactly when it
 *     applied an observed age (`offerWriteCore.cjs`, KB-1778) — and `observedBands.<side>AgeInYears` is stated: the
 *     writer applies observed ages side by side, so an unstated side stays the program's. When `observedBands` states
 *     no age on either side (absent, or grades only), both stored sides are carried under the flag alone (review
 *     round 2 N1). The core's `allowSessionAgeOverride` gate still decides, as it does for the writer;
 *   - grade: `<side>AgeGradeKey` + `<side>AgeGradeValue`, when the source stated that side's grade
 *     (`offers.observedBands.<side>AgeGradeKey`, KB-2145's provenance tag — the writer's own condition for applying a
 *     grade) and the stored side shows it (`show<Side>AgeGradeAsPrimary: true`, a key, a finite value). The show flag
 *     alone is not provenance: a program can set it, and the writer copies the program into every offer's
 *     `variationData` (review round 1 F1). The VALUE is the stored one, so an approved change survives.
 * Only keys that carry a value are returned. An offer with no `observedBands` keeps its stored ages under
 * `agesAreOverrides` and no grade (grades need a stated key). A write that states the band itself gets nothing — the
 * program's band: one carrying a `variationAgeOverrideInput`, or one that turns the stored override off. The caller
 * gates on a camp-data offer (`isCampDataOffer`).
 *
 * `observedBands` says WHICH sides; the stored `variationData` gives the VALUE. Not `observedBands`' numbers: it is
 * written at create and not refreshed (the pipeline's SET
 * ships OFF, KB-4618), so feeding it back would undo any later approved age change on every Publish — KB-3733's
 * failure, for ages. Offers a publish already reset are restored from `observedBands` by
 * `scripts/camp-data/_operator/repair-reset-session-ages.cjs`, once, under review.
 */
export function storedSessionAgeInputs(
  doc: Record<string, unknown>,
  write: { agesAreOverrides?: unknown; variationAgeOverrideInput?: unknown },
): Partial<CoreInput["form"]> {
  const agesAreOverrides = write.agesAreOverrides === true;
  // A staff statement in this write is the whole band (review round 1 F2): an explicit override (the seam applies it,
  // and a grade it turns off must not come back from the stored doc), or a save that turns the stored override off
  // (the session dialog's untick: back to the program's band). Nothing stored is carried.
  if (agesAreOverrides && write.variationAgeOverrideInput) return {};
  if (doc.agesAreOverrides === true && !agesAreOverrides) return {};
  const asRecord = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});
  const vd = asRecord(doc.variationData);
  const stated = asRecord(doc.observedBands);
  const finite = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const grade = (side: "min" | "max", Side: "Min" | "Max") => {
    const statedKey = stated[`${side}AgeGradeKey`];
    if (typeof statedKey !== "string" || !statedKey) return null;
    const key = vd[`${side}AgeGradeKey`];
    const value = finite(vd[`${side}AgeGradeValue`]);
    return vd[`show${Side}AgeGradeAsPrimary`] === true && typeof key === "string" && key && value != null ? { key, value } : null;
  };
  // Per side, only where the source stated that side's age: the writer applies observed ages side by side, so an
  // unstated side is the program's and follows it (review round 1 F3; the repair's `sideTarget` uses the same rule).
  // When `observedBands` states NEITHER age side, there is no stated side to separate from the program's, and the
  // override flag is the only provenance: both stored sides are carried (review round 2 N1, directive d3823711 —
  // Emerson Waldorf's per-session ages, written before `observedBands` recorded ages).
  const statesNoAge = finite(stated.minAgeInYears) == null && finite(stated.maxAgeInYears) == null;
  const age = (side: "min" | "max") =>
    agesAreOverrides && (statesNoAge || finite(stated[`${side}AgeInYears`]) != null) ? finite(vd[`${side}AgeInYears`]) : null;
  const out: Partial<CoreInput["form"]> = {};
  const minAge = age("min");
  const maxAge = age("max");
  if (minAge != null) out.observedMinAge = minAge;
  if (maxAge != null) out.observedMaxAge = maxAge;
  const minGrade = grade("min", "Min");
  const maxGrade = grade("max", "Max");
  if (minGrade) {
    out.observedMinGradeKey = minGrade.key;
    out.observedMinGradeValue = minGrade.value;
  }
  if (maxGrade) {
    out.observedMaxGradeKey = maxGrade.key;
    out.observedMaxGradeValue = maxGrade.value;
  }
  return out;
}
